/**
 * Стартовая страница оператора системной консоли.
 *
 * Мы целенаправленно ведём пользователя на «корень» консоли, а не на конкретный
 * подпуть. `SystemConsoleShell` сам подберёт первый доступный модуль из
 * :data:`SYSTEM_CONSOLE_MODULES` (по permission'ам) и сделает redirect. Так мы
 * не упираемся в 403, если у конкретного оператора нет именно `runtime-parameters`,
 * и позволяем добавлять/убирать модули в реестре без правок роутинга.
 */
export const SYSTEM_CONSOLE_DEFAULT_PATH = '/admin/system';

// Конфигурация прав доступа к маршрутам
export const ROUTE_ACCESS = {
  // Общие маршруты
  '/search': ['company', 'depot', 'customer'],
  '/notifications': ['company', 'depot', 'customer'],
  '/notifications/test': ['company', 'depot', 'customer'],
  // Admin-экраны уведомлений: доступ по роли/permission проверяется на бэке через
  // require_admin_role; на фронте разрешаем всем аутентифицированным типам профиля,
  // а сами admin-API ответят 403, если пользователь не админ.
  '/notifications/admin/queue': ['company', 'depot', 'customer'],
  '/notifications/admin/templates': ['company', 'depot', 'customer'],
  // Системная консоль: пункт меню / префиксы; guard маршрута — по permission, тип profile см. LazyRoute allowedTypes=[].
  '/admin/system': ['company', 'depot', 'customer', 'system_console'],
  '/settings': ['company', 'depot', 'customer'],
  '/customer/settings': ['company', 'depot', 'customer'],
  
  // Сообщения
  '/messages/inbox': ['company', 'depot', 'customer'],
  '/messages/sent': ['company', 'depot', 'customer'],
  '/messages/new': ['company', 'depot', 'customer'],
  '/messages/thread/:id': ['company', 'depot', 'customer'],
  '/messages/:id': ['company', 'depot', 'customer'],
  '/messages/:id/edit': ['company', 'depot', 'customer'],
  
  // Ремонты
  '/repairs': ['company', 'depot', 'customer'],
  '/repairs/current': ['company', 'depot', 'customer'],
  '/repairs/new': ['depot'],
  '/repairs/:id': ['company', 'depot', 'customer'],
  
  // Внешние закупки
  '/comings': ['company'],
  '/comings/new': ['company'],
  '/comings/:id': ['company'],
  '/comings/:id/edit': ['company'],
  
  // Документы
  '/documents': ['company', 'depot'],
  '/documents/new': ['depot'],
  '/documents/:id': ['company', 'depot'],
  '/documents/:id/edit': ['company', 'depot'],
  '/documents/:id/print': ['company', 'depot'],
  
  // Продажи
  '/sales': ['company', 'depot'],
  '/sales/new': ['company', 'depot'],
  '/sales/search': ['company', 'depot'],
  '/sales/:id': ['company', 'depot'],
  '/sales/:id/edit': ['company', 'depot'],
  
  // Заказы
  '/orders': ['company', 'depot', 'customer'],
  '/orders/new': ['depot'],
  '/orders/search': ['company', 'depot', 'customer'],
  '/orders/:id': ['company', 'depot', 'customer'],
  '/orders/:id/edit': ['company', 'depot'],
  
  // Справочники
  '/references/models': ['company'],
  '/references/models/new': ['company'],
  '/references/models/:uid': ['company'],
  '/references/categories': ['company'],
  '/references/categories/:uid': ['company'],
  '/references/spares': ['company'],
  '/references/spares/new': ['company'],
  '/references/spares/:uid/edit': ['company'],
  '/references/works': ['company'],
  '/references/works/new': ['company'],
  '/references/works/:uid/edit': ['company'],
  
  // Отчеты
  '/reports': ['company', 'depot'],
  '/reports/list': ['company', 'depot'],
  
  // Профили
  '/profiles': ['company', 'depot'],
  '/profiles/dedup': ['company', 'depot'],
  '/profiles/new': ['company', 'depot'],
  '/profiles/:uid': ['company', 'depot', 'customer'],
  '/profiles/:uid/complete': ['company', 'depot'],
  '/profiles/:uid/edit': ['company', 'depot'],
  
  // Возвраты
  '/returns': ['company', 'depot'],
  '/returns/new': ['depot'],
  '/returns/search': ['company', 'depot'],
  '/returns/:id': ['company', 'depot'],
  '/returns/:id/edit': ['company', 'depot'],

  // Заказы клиентов
  '/customer-orders': ['company', 'customer'],
  '/customer-orders/new': ['customer'],
  '/customer-orders/search': ['company', 'customer'],
  '/customer-orders/:id': ['company', 'customer'],
  '/customer-orders/:id/edit': ['company', 'customer'],
};

// Функция проверки доступа к маршруту
export const hasRouteAccess = (path, profileTypeCode) => {
  if (!path || !profileTypeCode) return false;
  
  // Сначала проверяем точное совпадение (приоритет выше)
  if (ROUTE_ACCESS[path]) {
    return ROUTE_ACCESS[path].includes(profileTypeCode);
  }

  // Затем проверяем динамические маршруты
  const dynamicRoute = Object.keys(ROUTE_ACCESS)
    .filter(routePattern => routePattern.includes(':'))
    .find(routePattern => {
      const regexPattern = routePattern
        .replace(/:[^/]+/g, '[^/]+')
        .replace(/\//g, '\\/');
      
      const regex = new RegExp(`^${regexPattern}$`);
      return regex.test(path);
    });
  
  if (dynamicRoute) {
    return ROUTE_ACCESS[dynamicRoute].includes(profileTypeCode);
  }
  
  // В самом конце проверяем совпадения по префиксу маршрута (наименее приоритетные)
  const matchingPrefixRoutes = Object.keys(ROUTE_ACCESS)
    .filter(routePattern => !routePattern.includes(':') && path.startsWith(`${routePattern}/`))
    .sort((a, b) => b.length - a.length); // Сортируем по длине, чтобы найти самый длинный префикс
  
  if (matchingPrefixRoutes.length > 0) {
    const bestPrefixMatch = matchingPrefixRoutes[0];
    return ROUTE_ACCESS[bestPrefixMatch].includes(profileTypeCode);
  }
  
  return false;
};

// Получение доступных маршрутов для типа профиля
export const getAvailableRoutes = (profileTypeCode) => {
  return Object.entries(ROUTE_ACCESS)
    .filter(([_, types]) => types.includes(profileTypeCode))
    .map(([route]) => route);
};

// Получение списка разрешенных типов профилей для маршрута
export const getAllowedTypes = (path) => {
  if (!path) return [];
  
  // Сначала проверяем точное совпадение
  if (ROUTE_ACCESS[path]) {
    return ROUTE_ACCESS[path];
  }
  
  // Затем проверяем динамические маршруты
  const dynamicRoute = Object.keys(ROUTE_ACCESS)
    .filter(routePattern => routePattern.includes(':'))
    .find(routePattern => {
      const regexPattern = routePattern
        .replace(/:[^/]+/g, '[^/]+')
        .replace(/\//g, '\\/');
      
      const regex = new RegExp(`^${regexPattern}$`);
      return regex.test(path);
    });
  
  if (dynamicRoute) {
    return ROUTE_ACCESS[dynamicRoute];
  }
  
  // В самом конце проверяем совпадения по префиксу маршрута
  const matchingPrefixRoutes = Object.keys(ROUTE_ACCESS)
    .filter(routePattern => !routePattern.includes(':') && path.startsWith(`${routePattern}/`))
    .sort((a, b) => b.length - a.length); // Сортируем по длине, чтобы найти самый длинный префикс
  
  if (matchingPrefixRoutes.length > 0) {
    const bestPrefixMatch = matchingPrefixRoutes[0];
    return ROUTE_ACCESS[bestPrefixMatch];
  }
  
  return [];
}; 